import { CloseIcon } from '@blocksuite/icons/lit';
import { addMinutes } from 'date-fns/addMinutes';
import { html, nothing, type TemplateResult } from 'lit';

import { dataViewI18nT } from '../../../../core/i18n.js';
import {
  type CalendarOccurrence,
  expandCalendarOccurrences,
  findCalendarConflicts,
} from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import { styles } from '../calendar-view-styles.js';
import type {
  CalendarEvent,
  CalendarReconcileState,
} from '../calendar-view-types.js';
import {
  formatCalendarDetailDateTime,
  renderAttendeesEditor,
  renderCalendarDateInput,
  renderCalendarInput,
  renderCalendarTextarea,
  renderExceptionsEditor,
  renderReadonlyCalendarDetails,
  renderRecurrenceEditor,
  renderReminderEditor,
  renderTimezoneInput,
} from './calendar-detail-panel.js';

type SetCellJson = (
  rowId: string,
  propertyId: string | undefined,
  value: unknown
) => void;

const MIN_DETAIL_EVENT_MINUTES = 15;

export function renderCalendarDetails({
  cancelOccurrence,
  close,
  deleteEvent,
  detachOccurrence,
  downloadEventIcs,
  event,
  events,
  hasPendingMailUpdates = false,
  occurrence,
  reconcileEventSideEffects,
  recurrenceError,
  rowId,
  setCalendarError,
  setCellJson,
  setCellDraftJson = setCellJson,
  commitCellChanges = () => {},
  syncState,
  view,
}: {
  cancelOccurrence: (event: CalendarOccurrence<CalendarEvent>) => void;
  close: () => void;
  deleteEvent: (rowId: string) => void;
  detachOccurrence: (event: CalendarOccurrence<CalendarEvent>) => void;
  downloadEventIcs: (event: CalendarEvent) => void;
  event: CalendarEvent;
  events: readonly CalendarOccurrence<CalendarEvent>[];
  hasPendingMailUpdates?: boolean;
  occurrence: CalendarOccurrence<CalendarEvent> | undefined;
  reconcileEventSideEffects: (event: CalendarEvent) => void;
  recurrenceError: string | undefined;
  rowId: string;
  setCalendarError: (message: string | undefined) => void;
  setCellDraftJson?: SetCellJson;
  setCellJson: SetCellJson;
  commitCellChanges?: (rowId: string) => void;
  syncState: CalendarReconcileState;
  view: CalendarSingleView;
}): TemplateResult {
  if (event.externalReadonly) {
    return renderReadonlyCalendarDetails({
      event,
      occurrence,
      onClose: close,
    });
  }
  const selectedOccurrence =
    occurrence ??
    expandCalendarOccurrences(
      recurrenceError ? { ...event, recurrence: '' } : event,
      event.startAt,
      event.endAt
    )[0];
  const conflicts = selectedOccurrence
    ? findCalendarConflicts(selectedOccurrence, events)
    : [];
  const readonly = view.readonly$.value;
  const detailContext = { event, readonly, rowId, setCellJson, view };
  const eventDurationMinutes = Math.max(
    MIN_DETAIL_EVENT_MINUTES,
    Math.round((event.endAt.getTime() - event.startAt.getTime()) / 60000)
  );
  const setStartAt = (value: Date) => {
    setCellJson(rowId, view.startDateCol$.value, value.getTime());
    const minimumEndAt = addMinutes(value, MIN_DETAIL_EVENT_MINUTES);
    if (event.endAt.getTime() < minimumEndAt.getTime()) {
      setCellJson(
        rowId,
        view.endDateCol$.value,
        addMinutes(value, eventDurationMinutes).getTime()
      );
    }
  };
  const setEndAt = (value: Date) => {
    const minimumEndAt = addMinutes(event.startAt, MIN_DETAIL_EVENT_MINUTES);
    setCellJson(
      rowId,
      view.endDateCol$.value,
      Math.max(value.getTime(), minimumEndAt.getTime())
    );
  };
  // Event placement is the containing database selected before creation.
  // calendarId remains provider/ICS routing metadata and must not become an
  // editable placement control here. RFC 5545 STATUS is lifecycle metadata,
  // not free/busy or attendee RSVP. CLASS visibility is provider round-trip
  // metadata, not StreamLain authorization. Both remain in the schema until
  // dedicated lifecycle/sharing UX exists.
  return html`<aside class="${styles.details}">
    <div class="${styles.detailsHeader}">
      <strong>${dataViewI18nT('calendar.detail.event')}</strong>
      <button
        class="${styles.iconButton}"
        aria-label=${dataViewI18nT('calendar.accessibility.closeDetails')}
        @click=${close}
      >
        ${CloseIcon({ height: '14px', width: '14px' })}
      </button>
    </div>
    <div class="${styles.detailsBody}" data-calendar-detail-scroll>
      ${occurrence?.isRecurring
        ? html`<div class="${styles.notice}">
            ${dataViewI18nT('calendar.detail.recurrenceOccurrence', {
              date: formatCalendarDetailDateTime(occurrence.startAt),
            })}
          </div>`
        : nothing}
      ${conflicts.length
        ? html`<div class="${styles.conflict}">
            ${dataViewI18nT(
              conflicts.length === 1
                ? 'calendar.detail.attendeeConflict'
                : 'calendar.detail.attendeeConflicts',
              {
                count: conflicts.length,
              }
            )}
          </div>`
        : nothing}
      ${recurrenceError
        ? html`<div class="${styles.conflict}">${recurrenceError}</div>`
        : nothing}
      ${syncState === 'error'
        ? html`<div class="${styles.conflict}">
            ${dataViewI18nT('calendar.detail.syncFailed')}
          </div>`
        : syncState === 'pending'
          ? html`<div class="${styles.notice}">
              ${dataViewI18nT('calendar.notice.savedOffline')}
            </div>`
          : nothing}
      ${renderCalendarInput(
        dataViewI18nT('calendar.detail.title'),
        event.title,
        value =>
          setCellDraftJson(
            rowId,
            view.mainProperties$.value.titleColumn,
            value
          ),
        'text',
        readonly,
        () => commitCellChanges(rowId)
      )}
      <div class="${styles.formGrid}">
        ${renderCalendarDateInput(
          dataViewI18nT('calendar.detail.start'),
          event.startAt,
          setStartAt,
          readonly
        )}
        ${renderCalendarDateInput(
          dataViewI18nT('calendar.detail.end'),
          event.endAt,
          setEndAt,
          readonly
        )}
      </div>
      <label class="${styles.checkboxRow}">
        <input
          type="checkbox"
          .checked=${event.allDay}
          ?disabled=${readonly}
          @change=${(change: Event) => {
            const input = change.currentTarget as HTMLInputElement;
            setCellJson(rowId, view.allDayCol$.value, input.checked);
          }}
        />
        <span>${dataViewI18nT('calendar.detail.allDay')}</span>
      </label>
      ${renderCalendarInput(
        dataViewI18nT('calendar.detail.address'),
        event.locationPhysical,
        value =>
          setCellDraftJson(rowId, view.locationPhysicalCol$.value, value),
        'text',
        readonly,
        () => commitCellChanges(rowId)
      )}
      ${renderCalendarInput(
        dataViewI18nT('calendar.detail.virtualLink'),
        event.locationVirtual,
        value => setCellDraftJson(rowId, view.locationVirtualCol$.value, value),
        'url',
        readonly,
        () => commitCellChanges(rowId)
      )}
      <div class="${styles.sectionDivider}">
        ${renderCalendarTextarea(
          dataViewI18nT('calendar.detail.description'),
          event.description,
          value => setCellDraftJson(rowId, view.descriptionCol$.value, value),
          readonly,
          () => commitCellChanges(rowId)
        )}
      </div>
      ${renderAttendeesEditor(detailContext)}
      <div class="${styles.sectionDivider}">
        ${renderReminderEditor(detailContext)}
        ${renderRecurrenceEditor({
          ...detailContext,
          setCalendarError,
        })}
        ${event.recurrenceId
          ? renderCalendarInput(
              dataViewI18nT('calendar.detail.recurrenceId'),
              event.recurrenceId,
              value =>
                setCellDraftJson(rowId, view.recurrenceIdCol$.value, value),
              'text',
              readonly,
              () => commitCellChanges(rowId)
            )
          : nothing}
        ${renderExceptionsEditor(detailContext)}
        <details class="${styles.advancedSection}">
          <summary>${dataViewI18nT('calendar.wizard.advancedTitle')}</summary>
          <div class="${styles.advancedSectionBody}">
            ${renderTimezoneInput(
              event.timezone,
              value => setCellDraftJson(rowId, view.timezoneCol$.value, value),
              readonly,
              () => commitCellChanges(rowId)
            )}
          </div>
        </details>
      </div>
    </div>
    <div class="${styles.detailsActions}">
      ${hasPendingMailUpdates
        ? html`<button
            class="${styles.primaryButton}"
            aria-label=${dataViewI18nT('calendar.accessibility.sendUpdates')}
            ?disabled=${view.readonly$.value ||
            syncState === 'syncing' ||
            Boolean(recurrenceError)}
            @click=${() => reconcileEventSideEffects(event)}
          >
            ${syncState === 'syncing'
              ? dataViewI18nT('calendar.action.sending')
              : dataViewI18nT('calendar.action.sendUpdates')}
          </button>`
        : nothing}
      <button
        class="${styles.todayButton}"
        aria-label=${dataViewI18nT('calendar.accessibility.exportEvent')}
        ?disabled=${Boolean(recurrenceError)}
        @click=${() => downloadEventIcs(event)}
      >
        ${dataViewI18nT('calendar.action.exportIcs')}
      </button>
      ${occurrence?.isRecurring
        ? html`<button
              class="${styles.todayButton}"
              aria-label=${dataViewI18nT(
                'calendar.accessibility.editOccurrence'
              )}
              ?disabled=${view.readonly$.value}
              @click=${() => detachOccurrence(occurrence)}
            >
              ${dataViewI18nT('calendar.action.editOccurrence')}
            </button>
            <button
              class="${styles.todayButton}"
              aria-label=${dataViewI18nT(
                'calendar.accessibility.cancelOccurrence'
              )}
              ?disabled=${view.readonly$.value}
              @click=${() => cancelOccurrence(occurrence)}
            >
              ${dataViewI18nT('calendar.action.cancelOccurrence')}
            </button>`
        : nothing}
      <button
        class="${styles.dangerButton}"
        aria-label=${dataViewI18nT('calendar.accessibility.deleteEvent')}
        ?disabled=${view.readonly$.value}
        @click=${() => deleteEvent(rowId)}
      >
        ${dataViewI18nT('calendar.action.delete')}
      </button>
    </div>
  </aside>`;
}
